import { countStacksUse, StacksBySite } from '@pubstack/common/src/stack'
import { Button } from '@pubstack/ui/components/Button'
import { cn } from '@pubstack/ui/utils/cn'
import { FunctionComponent } from 'react'
import { WithClassName } from '~/types/utils'

export const pluralize = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`

/** A media key with an empty list is not a use, the same way `countStacksUse` does not count it. */
export const countMediasUse = (stacksUse?: StacksBySite): number => Object.values(stacksUse ?? {}).filter((stacks) => stacks.length > 0).length

type StackUsageProps = WithClassName & {
  stacksUse?: StacksBySite
  /** Swaps the flyout for the full list: the flyout itself only counts. */
  onSeeAll: () => void
}

/**
 * Where a rule, a context or an ad unit is used, as every view flyout shows it: two counts and the way to
 * the list. The counts answer "is it safe to change?" on sight; the names live in `StackUsageDetails`,
 * where an item on a hundred medias does not bury the flyout's own content.
 */
export const StackUsage: FunctionComponent<StackUsageProps> = ({ className, stacksUse, onSeeAll }) => {
  const stackCount = countStacksUse(stacksUse)

  return (
    /* Rendered even when empty — "used by nothing" is the one answer a publisher about to change
       something most wants, and hiding the section withholds it. */
    <section className={cn('flex flex-col gap-2', className)}>
      <div className={'flex items-center justify-between gap-2'}>
        <h3 className={'m-0 font-medium'}>
          Used by {pluralize(countMediasUse(stacksUse), 'media')}
          {stackCount > 0 && <span className={'font-normal text-slate-500'}>{` · ${pluralize(stackCount, 'stack')}`}</span>}
        </h3>
        {stackCount > 0 && (
          <Button variant={'tertiary'} size={'s'} onClick={onSeeAll} trackingLabel={'common-stack-usage-view'}>
            See all usage
          </Button>
        )}
      </div>
      {stackCount === 0 && <span className={'text-slate-grey'}>Not used by any stack.</span>}
    </section>
  )
}
